import { useEffect, useState } from "react";
import { Plus, Pencil, ShieldCheck, Mail, Loader2, Copy, SlidersHorizontal, ImagePlus } from "lucide-react";
import { setBranding, getBranding } from "@/lib/branding";
import { applyBrandColor, contrastWithWhite, DEFAULT_BRAND } from "@/lib/brandColor";
import { imageUrl } from "@/lib/siteContent";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, useApi, fmtDT } from "@/lib/api";
import { Field, Card, Table, Th, Td, Pill } from "@/components/kit";

const BTN = "ka-press inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-60";
const BLANK = { name: "", description: "", price_per_venue: 69, features: [], active: true, sort_order: 0 };

function PackageDialog({ pkg, onClose, onSaved }) {
  const [f, setF] = useState({ ...BLANK, ...pkg, featuresText: (pkg?.features || []).join("\n") });
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async (e) => {
    e.preventDefault();
    const body = { name: f.name, description: f.description, price_per_venue: Number(f.price_per_venue), active: f.active,
      sort_order: Number(f.sort_order), features: f.featuresText.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 15) };
    try {
      if (pkg?.id) await api.put(`/platform/packages/${pkg.id}`, body);
      else await api.post("/platform/packages", body);
      toast.success("Package saved");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">{pkg?.id ? "Edit package" : "New package"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
          <Field label="Name *" className="sm:col-span-2"><input data-testid="package-name-input" required minLength={2} className="ka-input" value={f.name} onChange={(e) => set("name", e.target.value)} /></Field>
          <Field label="Price per venue / month (£) *"><input data-testid="package-price-input" type="number" step="0.01" min={0.5} required className="ka-input" value={f.price_per_venue} onChange={(e) => set("price_per_venue", e.target.value)} /></Field>
          <Field label="Display order" hint="Lowest shows first"><input data-testid="package-order-input" type="number" min={0} className="ka-input" value={f.sort_order} onChange={(e) => set("sort_order", e.target.value)} /></Field>
          <Field label="Description" className="sm:col-span-2"><textarea data-testid="package-description-input" rows={2} className="ka-input" value={f.description} onChange={(e) => set("description", e.target.value)} /></Field>
          <Field label="Features (one per line)" className="sm:col-span-2"><textarea data-testid="package-features-input" rows={4} className="ka-input" value={f.featuresText} onChange={(e) => set("featuresText", e.target.value)} /></Field>
          <label className="flex items-center gap-2 text-sm sm:col-span-2"><input data-testid="package-active-checkbox" type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} />Available for businesses to choose</label>
          <button data-testid="save-package-button" className={`${BTN} justify-center bg-emerald-600 text-white hover:bg-emerald-700 sm:col-span-2`}>Save package</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function PackagesEditor() {
  const { data, reload } = useApi("/platform/packages");
  const [edit, setEdit] = useState(null);
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Businesses choose a package on their Billing page. Stripe charges the price set here per venue each month.</p>
        <button data-testid="add-package-button" onClick={() => setEdit({})} className={`${BTN} bg-emerald-600 text-white hover:bg-emerald-700`}><Plus className="h-4 w-4" />New package</button>
      </div>
      <Table testid="packages-table">
        <thead><tr><Th>Package</Th><Th>Price / venue</Th><Th>Features</Th><Th>Businesses</Th><Th>Status</Th><Th className="text-right">Edit</Th></tr></thead>
        <tbody>
          {(data || []).map((p) => (
            <tr key={p.id} data-testid={`package-row-${p.id}`}>
              <Td className="font-semibold text-slate-900">{p.name}<div className="text-xs font-normal text-slate-500">{p.description}</div></Td>
              <Td>£{p.price_per_venue.toFixed(2)}</Td>
              <Td className="text-xs">{p.features.length}</Td>
              <Td>{p.businesses}</Td>
              <Td><Pill status={p.active ? "ok" : "low"} label={p.active ? "Available" : "Hidden"} /></Td>
              <Td className="text-right"><button data-testid={`edit-package-${p.id}`} onClick={() => setEdit(p)} className="ka-press rounded-full border border-slate-300 p-2 hover:border-slate-900"><Pencil className="h-3.5 w-3.5" /></button></Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {edit && <PackageDialog pkg={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </div>
  );
}

export function PaymentsLedger() {
  const { data } = useApi("/platform/payments");
  const rows = data || [];
  return (
    <Table testid="payments-ledger">
      <thead><tr><Th>Date</Th><Th>Business</Th><Th>Type</Th><Th>Package / method</Th><Th>Venues</Th><Th>Amount</Th><Th>Payment</Th><Th>Subscription</Th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={`${r.kind}-${r.id}`} data-testid={`payment-row-${r.id}`}>
            <Td className="whitespace-nowrap text-xs">{fmtDT(r.created_at)}</Td>
            <Td className="font-semibold text-slate-900">{r.business}</Td>
            <Td className="capitalize">{r.kind}</Td>
            <Td className="capitalize">{r.package}</Td>
            <Td>{r.venues}</Td>
            <Td>£{r.amount.toFixed(2)}</Td>
            <Td><Pill status={r.status === "paid" ? "ok" : "due"} label={r.status} /></Td>
            <Td className="capitalize">{(r.subscription_status || "—").replace("_", " ")}</Td>
          </tr>
        ))}
        {data && !rows.length && <tr><Td className="text-slate-500">No payments yet.</Td></tr>}
      </tbody>
    </Table>
  );
}

function useSettings(path) {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get(path).then((r) => setS(r.data));
  }, [path]);
  return [s, setS];
}

function WebhookHelp() {
  const { data } = useApi("/platform/stripe/webhook-events");
  const url = `${window.location.origin}/api/stripe/webhook`;
  const copy = (t, m) => { navigator.clipboard.writeText(t); toast.success(m); };
  return (
    <div data-testid="stripe-webhook-help" className="rounded-xl bg-slate-50 p-4 text-sm sm:col-span-2">
      <div className="font-semibold text-slate-900">Webhook setup</div>
      <p className="mt-1 text-xs text-slate-600">In Stripe go to Developers → Webhooks → Add endpoint. Do this once in Test mode and once in Live mode, and paste each signing secret (whsec_…) above.</p>
      <div className="mt-3 flex gap-2"><code data-testid="stripe-webhook-url" className="min-w-0 flex-1 truncate rounded-lg bg-white px-3 py-2 font-mono text-xs ring-1 ring-slate-200">{url}</code><button type="button" data-testid="stripe-webhook-url-copy" onClick={() => copy(url, "Webhook URL copied")} className="ka-press rounded-full border border-slate-300 bg-white px-3"><Copy className="h-4 w-4" /></button></div>
      <div className="mt-3 flex items-center justify-between"><span className="text-xs font-semibold text-slate-700">Events to select ({data?.length || 0})</span><button type="button" data-testid="stripe-webhook-events-copy" onClick={() => copy((data || []).map((e) => e.event).join("\n"), "Event list copied")} className="text-xs font-semibold text-emerald-700 hover:underline">Copy all</button></div>
      <ul data-testid="stripe-webhook-events" className="mt-2 divide-y divide-slate-200">{(data || []).map((e) => <li key={e.event} className="flex flex-wrap items-baseline gap-x-3 py-1.5"><code className="font-mono text-xs font-bold text-slate-900">{e.event}</code><span className="text-xs text-slate-500">{e.why}</span></li>)}</ul>
    </div>
  );
}

function StripeSettings() {
  const [s, setS] = useSettings("/platform/settings/stripe");
  const [f, setF] = useState({ test_secret_key: "", live_secret_key: "", test_webhook_secret: "", live_webhook_secret: "" });
  const [busy, setBusy] = useState("");
  if (!s) return null;
  const save = async (e) => {
    e.preventDefault();
    setBusy("save");
    try {
      const body = { mode: s.mode, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v.trim()).map(([k, v]) => [k, v.trim()])) };
      const { data } = await api.put("/platform/settings/stripe", body);
      setS(data);
      setF({ test_secret_key: "", live_secret_key: "", test_webhook_secret: "", live_webhook_secret: "" });
      toast.success("Stripe settings saved");
    } catch (err) {
      toast.error(errMsg(err));
      api.get("/platform/settings/stripe").then((r) => setS(r.data));
    } finally {
      setBusy("");
    }
  };
  const test = async () => {
    setBusy("test");
    try {
      const { data } = await api.post("/platform/settings/stripe/test");
      toast.success(`Connected to Stripe account ${data.account_id}${data.country ? ` (${data.country})` : ""}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy("");
    }
  };
  const key = (k, label, ph) => (
    <Field label={label} hint={s[k] ? `Saved: ${s[k]} — leave blank to keep` : k === "test_secret_key" && s.using_default_test_key ? "Using the built-in test key" : "Not set"}>
      <input data-testid={`stripe-${k}`} type="password" autoComplete="off" className="ka-input font-mono" placeholder={ph} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
    </Field>
  );
  return (
    <Card>
      <div className="mb-4 flex items-center gap-2 font-display text-xl font-extrabold"><ShieldCheck className="h-5 w-5 text-emerald-600" />Stripe</div>
      <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
        <Field label="Mode" className="sm:col-span-2">
          <div className="flex gap-2">{["test", "live"].map((m) => <button type="button" key={m} data-testid={`stripe-mode-${m}`} onClick={() => setS({ ...s, mode: m })} className={`ka-press rounded-full px-4 py-1.5 text-sm font-semibold capitalize ${s.mode === m ? (m === "live" ? "bg-rose-600 text-white" : "bg-slate-900 text-white") : "bg-slate-100 text-slate-600"}`}>{m}</button>)}</div>
        </Field>
        {key("test_secret_key", "Test secret key", "sk_test_…")}
        {key("test_webhook_secret", "Test webhook secret", "whsec_…")}
        {key("live_secret_key", "Live secret key", "sk_live_…")}
        {key("live_webhook_secret", "Live webhook secret", "whsec_…")}
        <p className="text-xs text-slate-500 sm:col-span-2">Keys are stored encrypted and never shown again in full.</p>
        <WebhookHelp />
        <div className="flex flex-wrap gap-2 sm:col-span-2">
          <button data-testid="stripe-save-button" disabled={!!busy} className={`${BTN} bg-emerald-600 text-white hover:bg-emerald-700`}>{busy === "save" && <Loader2 className="h-4 w-4 animate-spin" />}Save Stripe settings</button>
          <button type="button" data-testid="stripe-test-button" disabled={!!busy} onClick={test} className={`${BTN} border border-slate-300 hover:border-slate-900`}>{busy === "test" && <Loader2 className="h-4 w-4 animate-spin" />}Test connection</button>
        </div>
      </form>
    </Card>
  );
}

function SmtpSettings() {
  const [s, setS] = useSettings("/platform/settings/smtp");
  const [pw, setPw] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState("");
  if (!s) return null;
  const set = (k, v) => setS({ ...s, [k]: v });
  const run = async (kind, fn) => {
    setBusy(kind);
    try {
      await fn();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy("");
    }
  };
  const save = (e) => {
    e.preventDefault();
    run("save", async () => {
      const { data } = await api.put("/platform/settings/smtp", { host: s.host, port: Number(s.port), security: s.security, username: s.username,
        from_email: s.from_email, from_name: s.from_name, site_url: (s.site_url || "").trim(), ...(pw ? { password: pw } : {}) });
      setS(data);
      setPw("");
      toast.success("Email settings saved");
    });
  };
  const test = () => run("test", async () => {
    await api.post("/platform/settings/smtp/test", { to });
    toast.success(`Test email sent to ${to}`);
  });
  return (
    <Card>
      <div className="mb-4 flex items-center gap-2 font-display text-xl font-extrabold"><Mail className="h-5 w-5 text-emerald-600" />Platform email (SMTP)</div>
      <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
        <Field label="SMTP host *"><input data-testid="smtp-host-input" required className="ka-input" placeholder="smtp.yourmail.co.uk" value={s.host} onChange={(e) => set("host", e.target.value)} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Port"><input data-testid="smtp-port-input" type="number" required className="ka-input" value={s.port} onChange={(e) => set("port", e.target.value)} /></Field>
          <Field label="Security"><select data-testid="smtp-security-select" className="ka-input" value={s.security} onChange={(e) => set("security", e.target.value)}><option value="ssl">SSL (465)</option><option value="starttls">STARTTLS (587)</option><option value="none">None</option></select></Field>
        </div>
        <Field label="Username"><input data-testid="smtp-username-input" autoComplete="off" className="ka-input" value={s.username} onChange={(e) => set("username", e.target.value)} /></Field>
        <Field label="Password" hint={s.password ? `Saved: ${s.password} — leave blank to keep` : "Not set"}><input data-testid="smtp-password-input" type="password" autoComplete="new-password" className="ka-input" value={pw} onChange={(e) => setPw(e.target.value)} /></Field>
        <Field label="From email *"><input data-testid="smtp-from-email-input" type="email" required className="ka-input" value={s.from_email} onChange={(e) => set("from_email", e.target.value)} /></Field>
        <Field label="From name"><input data-testid="smtp-from-name-input" className="ka-input" value={s.from_name} onChange={(e) => set("from_name", e.target.value)} /></Field>
        <Field label="App web address" hint="Used for links in emails, e.g. https://kitchenaudit.app" className="sm:col-span-2"><input data-testid="smtp-site-url-input" type="url" className="ka-input" placeholder={window.location.origin} value={s.site_url || ""} onChange={(e) => set("site_url", e.target.value)} /></Field>
        <button data-testid="smtp-save-button" disabled={!!busy} className={`${BTN} justify-self-start bg-emerald-600 text-white hover:bg-emerald-700 sm:col-span-2`}>{busy === "save" && <Loader2 className="h-4 w-4 animate-spin" />}Save email settings</button>
      </form>
      <div className="mt-6 flex flex-wrap items-end gap-2 border-t border-slate-200 pt-4">
        <Field label="Send a test email to" className="min-w-[220px] flex-1"><input data-testid="smtp-test-to-input" type="email" className="ka-input" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        <button data-testid="smtp-test-button" disabled={!!busy || !s.configured || !to} onClick={test} className={`${BTN} border border-slate-300 hover:border-slate-900`}>{busy === "test" && <Loader2 className="h-4 w-4 animate-spin" />}Send test</button>
      </div>
    </Card>
  );
}

function LogoField({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/platform/site-images", fd);
      onChange(data.url);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Field label="Platform logo" hint="Shown on the website, sign-in pages and the app menu. A wide PNG with a transparent background works best.">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex h-16 w-48 items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 p-2">{value ? <img data-testid="logo-preview" src={imageUrl(value)} alt="Logo" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-slate-400">Using the default logo</span>}</div>
        <label data-testid="logo-upload" className="ka-press inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-semibold hover:border-slate-900">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}{busy ? "Uploading…" : "Upload logo"}<input data-testid="logo-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={busy} onChange={pick} /></label>
        {value && <button type="button" data-testid="logo-reset" onClick={() => onChange("")} className="text-xs font-semibold text-slate-500 hover:text-rose-600">Use default</button>}
      </div>
    </Field>
  );
}

const SWATCHES = ["#059669", "#0284c7", "#4f46e5", "#c026d3", "#e11d48", "#ea580c", "#ca8a04", "#0f172a"];

function ColorField({ value, onChange }) {
  const hex = value || DEFAULT_BRAND;
  const pick = (v) => { applyBrandColor(v); onChange(v === DEFAULT_BRAND ? "" : v); };
  const low = contrastWithWhite(hex) < 3;
  return (
    <Field label="Highlight colour" hint="Used for buttons, links, highlights and charts across the website and every business app.">
      <div className="flex flex-wrap items-center gap-2">
        {SWATCHES.map((s) => <button type="button" key={s} data-testid={`brand-swatch-${s.slice(1)}`} onClick={() => pick(s)} aria-label={`Use ${s}`} className={`h-8 w-8 rounded-full ring-offset-2 transition-transform hover:scale-110 ${hex.toLowerCase() === s ? "ring-2 ring-slate-900" : ""}`} style={{ background: s }} />)}
        <label className="ka-press inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-300 bg-white py-1 pl-1 pr-3 text-xs font-semibold hover:border-slate-900"><input data-testid="brand-color-input" type="color" value={hex} onChange={(e) => pick(e.target.value)} className="h-6 w-6 cursor-pointer rounded-full border-0 bg-transparent p-0" />Custom</label>
        <input data-testid="brand-color-hex" className="ka-input w-28 font-mono uppercase" value={hex} maxLength={7} onChange={(e) => /^#[0-9a-fA-F]{6}$/.test(e.target.value) && pick(e.target.value.toLowerCase())} />
        {value && <button type="button" data-testid="brand-color-reset" onClick={() => pick(DEFAULT_BRAND)} className="text-xs font-semibold text-slate-500 hover:text-rose-600">Back to green</button>}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="brand-color-preview">
        <span className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold text-white">Button</span>
        <span className="text-sm font-semibold text-emerald-700">Link text</span>
        <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-200">Highlight</span>
      </div>
      {low && <p data-testid="brand-color-warning" className="mt-2 text-xs text-amber-700">This colour is quite light, so white text on buttons may be hard to read. A darker shade works better.</p>}
    </Field>
  );
}

function GeneralSettings() {
  const { data } = useApi("/public/branding");
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => () => { applyBrandColor(getBranding()?.brand_color); }, []);
  const s = f || data;
  if (!s) return null;
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: out } = await api.put("/platform/settings/general", { trial_days: Number(s.trial_days), logo_url: s.logo_url || "", brand_color: s.brand_color || "" });
      setBranding(out);
      setF(out);
      toast.success("General settings saved");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="xl:col-span-2">
      <div className="mb-4 flex items-center gap-2 font-display text-xl font-extrabold"><SlidersHorizontal className="h-5 w-5 text-emerald-600" />General</div>
      <form onSubmit={save} className="grid gap-4 sm:grid-cols-[200px_1fr]">
        <Field label="Free trial (days)" hint="For new sign-ups. Existing trials keep their end date."><input data-testid="trial-days-input" type="number" min={1} max={365} required className="ka-input" value={s.trial_days} onChange={(e) => setF({ ...s, trial_days: e.target.value })} /></Field>
        <LogoField value={s.logo_url} onChange={(v) => setF({ ...s, logo_url: v })} />
        <div className="sm:col-span-2"><ColorField value={s.brand_color} onChange={(v) => setF({ ...s, brand_color: v })} /></div>
        <button data-testid="general-save-button" disabled={busy} className={`${BTN} justify-self-start bg-emerald-600 text-white hover:bg-emerald-700 sm:col-span-2`}>{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save general settings</button>
      </form>
    </Card>
  );
}

export function PlatformSettingsPanel() {
  return <div className="grid gap-6 xl:grid-cols-2"><GeneralSettings /><StripeSettings /><SmtpSettings /></div>;
}
